/**
 * How a month actually went, for one client — including their half of it.
 *
 * Written because the month report is the one everybody agrees matters and
 * nobody has time to write: a day per client, every month, assembled by hand
 * from the same rows this platform already holds. So it is computed, and the
 * only work left is reading it.
 *
 * The `theirs` column is the part that is not in any other tool. Every
 * platform can tell a client what the agency delivered; this one knows how
 * many days each piece spent waiting on *them*, because `sent_to_client_at`
 * and `client_responded_at` are already on the row. That is what makes this
 * something the studio can send rather than only read — a report that can
 * only ever blame the agency does not get sent twice.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronDown, Copy, FileText } from 'lucide-react'
import { useState } from 'react'
import { Button } from './Button'
import { useToast } from '../context/ToastContext'
import { useApi } from '../useApi'

interface Promised {
  metric: string
  unit: string
  target_count: number
  delivered: number
  difference: number
}

interface Recap {
  client_id: string
  client_name: string
  month: string
  closed_at: string | null
  promised: Promised[]
  ours: { delivered: number; late: number; outstanding: number; unapproved: number }
  theirs: {
    sent: number
    answered: number
    waiting: number
    days_held: number
    longest_wait: number
    chases: number
  }
  headline: string
}

function monthName(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (y === undefined || m === undefined) return month
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

/** The recap as text, for pasting into the thread it is going to be sent in. */
function asText(r: Recap): string {
  const lines = [`${r.client_name} — ${monthName(r.month)}`, '', r.headline, '']
  for (const p of r.promised) {
    const gap = p.difference === 0 ? '' : p.difference > 0 ? ` (+${p.difference})` : ` (${p.difference})`
    lines.push(`• ${p.metric}: ${p.delivered} of ${p.target_count} ${p.unit}${gap}`)
  }
  if (r.theirs.sent > 0) {
    lines.push('')
    lines.push(
      `Approvals: ${r.theirs.answered} of ${r.theirs.sent} answered` +
        (r.theirs.waiting > 0 ? `, ${r.theirs.waiting} still open` : '') +
        (r.theirs.longest_wait >= 1 ? `. Longest wait ${Math.round(r.theirs.longest_wait)} days.` : '.'),
    )
  }
  return lines.join('\n')
}

export function MonthRecap({ clientId, month }: { clientId: string; month: string }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-2xl border border-canvas-fg/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2.5 px-4 py-3 text-left text-sm font-medium text-canvas-fg"
      >
        <FileText size={15} className="shrink-0 text-canvas-fg/55" />
        How {monthName(month)} went
        <ChevronDown
          size={15}
          className={`ml-auto shrink-0 text-canvas-fg/45 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="border-t border-canvas-fg/10 px-4 py-4">
              <Body clientId={clientId} month={month} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * The report itself, as its own component so that opening the panel is what
 * fetches it.
 *
 * The month view above already loads the whole sheet, and this is an aggregate
 * nobody has asked for until they click. Mounting it on open gets that for
 * free. Handing the fetch hook a conditional path would do the same thing, but
 * it would also make this a route no static read of the app can follow, which
 * is the hole the route tests exist to refuse.
 */
function Body({ clientId, month }: { clientId: string; month: string }) {
  const recapPath = `/clients/${encodeURIComponent(clientId)}/months/${encodeURIComponent(month)}/recap`
  const { result } = useApi<Recap>(recapPath)
  const [copied, setCopied] = useState(false)
  const { showToast } = useToast()

  const recap = result.state === 'loaded' ? result.data : null

  async function copy() {
    if (recap === null) return
    try {
      await navigator.clipboard.writeText(asText(recap))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      showToast('Could not reach the clipboard. Select the text and copy it.', 'warning')
    }
  }

  return (
    <>
      {result.state === 'loading' && <p className="text-sm text-canvas-fg/55">Working it out…</p>}
      {result.state === 'failed' && <p className="text-sm text-canvas-fg/55">{result.error.message}</p>}
      {recap !== null && (
        <div className="flex flex-col gap-4">
          <p className="font-display text-base font-semibold text-canvas-fg">{recap.headline}</p>

          {recap.promised.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {recap.promised.map((p) => (
                <li key={p.metric} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate text-canvas-fg/75">{p.metric}</span>
                  <span className="shrink-0 font-medium tabular-nums text-canvas-fg">
                    {p.delivered} / {p.target_count} {p.unit}
                    {p.difference < 0 && <span className="ml-1.5 text-danger-500">{p.difference}</span>}
                    {p.difference > 0 && <span className="ml-1.5 text-success-500">+{p.difference}</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {/*
            * Two columns, ours first. The ordering is the whole point: a recap
            * that opens with how slow the client was is a recap that gets sent
            * once. Said second, the same numbers read as a fact rather than a
            * rebuttal.
            */}
          <div className="grid grid-cols-2 gap-3">
            <Side
              heading="Us"
              rows={[
                ['Went out', recap.ours.delivered],
                ['Later than planned', recap.ours.late],
                ['Still open', recap.ours.outstanding],
                ['Posted unapproved', recap.ours.unapproved],
              ]}
            />
            <Side
              heading="Them"
              rows={[
                ['Sent for approval', recap.theirs.sent],
                ['Answered', recap.theirs.answered],
                ['Still waiting', recap.theirs.waiting],
                ['Times we asked', recap.theirs.chases],
                ['Longest wait', recap.theirs.longest_wait >= 1 ? `${Math.round(recap.theirs.longest_wait)}d` : '—'],
              ]}
            />
          </div>

          <div>
            <Button variant="secondary" size="sm" onClick={() => void copy()}>
              {copied ? <Check size={13} className="mr-1.5" /> : <Copy size={13} className="mr-1.5" />}
              {copied ? 'Copied' : 'Copy for the client'}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

function Side({ heading, rows }: { heading: string; rows: Array<[string, number | string]> }) {
  return (
    <div className="rounded-xl bg-canvas-fg/[0.04] px-3 py-2.5">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-canvas-fg/50">{heading}</p>
      <ul className="flex flex-col gap-1">
        {rows.map(([label, value]) => (
          <li key={label} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="min-w-0 truncate text-canvas-fg/65">{label}</span>
            <span className="shrink-0 font-medium tabular-nums text-canvas-fg">{value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
